import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import { Button, Form, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import { FaInfoCircle } from "react-icons/fa";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import NewJoinerDeviceDeploymentCard from "./NewJoinerDeviceDeploymentCard";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, ChartTooltip, ChartLegend);

// The 6-Month Trend line chart's hover tooltip already showed each point's
// value, but only while actively hovering -- the numbers themselves were
// otherwise invisible on the chart. This draws each point's own value
// permanently above it (in that dataset's own color), so the numbers are
// readable at a glance without hovering. Scoped to just this chart instance
// via the `plugins` prop below (not ChartJS.register), so it never affects
// any other chart elsewhere in the app.
const pointValueLabelPlugin = {
  id: "pointValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      const isPercent = dataset.yAxisID === "percent";
      meta.data.forEach((point, index) => {
        const value = dataset.data[index];
        if (value === null || value === undefined || Number.isNaN(Number(value))) return;
        const rounded = Math.round(Number(value) * 10) / 10;
        const text = isPercent ? `${rounded}%` : `${rounded}`;
        ctx.save();
        ctx.font = "10px sans-serif";
        ctx.fillStyle = dataset.borderColor || "#111827";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(text, point.x, point.y - 6);
        ctx.restore();
      });
    });
  },
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function currentMonthYear() {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

function yearOptions(currentYear) {
  const years = [];
  for (let y = currentYear; y >= currentYear - 4; y--) years.push(y);
  return years;
}

function monthKey(month, year) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function shiftMonth(month, year, delta) {
  const d = new Date(year, month - 1 + delta, 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

// Financial year convention already established elsewhere in this app
// (PerformancePeriodManagementPage.jsx): April - March.
function financialYearRange(month, year) {
  const fyStartYear = month >= 4 ? year : year - 1;
  return { from: { month: 4, year: fyStartYear }, to: { month: 3, year: fyStartYear + 1 } };
}

function formatMonthLabel(month, year) {
  return `${MONTH_NAMES[month - 1].slice(0, 3)} ${year}`;
}

function InfoTooltip({ text }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <span className="tpp-info" role="img" aria-label="How this is calculated">
        <FaInfoCircle />
      </span>
    </OverlayTrigger>
  );
}

// Plain <div> by default (unchanged for the 3 existing ticket-metric
// cards); passing `onClick` renders the SAME markup/classes as a real
// <button> instead, so a clickable KPI (e.g. Preventive Compliance ->
// /technician/preventive-maintenance) is a proper, keyboard-accessible
// button rather than a clickable div.
function KpiCard({ title, value, subtext, note, tooltip, accent, onClick }) {
  const className = `td-card tpp-kpi-card${accent ? ` tpp-kpi-card--${accent}` : ""}${onClick ? " tpp-kpi-card--clickable" : ""}`;
  const content = (
    <>
      <div className="tpp-kpi-head">
        <span className="tpp-kpi-title">{title}</span>
        <InfoTooltip text={tooltip} />
      </div>
      <div className="tpp-kpi-value">{value}</div>
      <div className="tpp-kpi-sub">{subtext}</div>
      {note && <div className="tpp-kpi-note">{note}</div>}
    </>
  );

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}

// N/A (not 0%) when preventive checks aren't configured/available -- never
// a divide-by-zero. Green only at 100%; otherwise amber, never red/danger,
// so a current-month partial/pending state never reads as a "failure"
// before the month has even ended (see preventiveMaintenanceService's own
// current-month PENDING vs past-month MISSED rules -- this card just
// displays what that service already decided, never re-derives it).
function buildPreventiveCard(pm) {
  if (!pm || !pm.available) {
    return {
      value: "N/A",
      subtext: "No preventive checks configured",
      note: null,
      accent: "amber",
    };
  }

  const { required, completed, pending, missed, compliance_percent } = pm;
  const accent = compliance_percent === 100 ? "green" : "amber";
  const subtext = `${completed} of ${required} check${required === 1 ? "" : "s"} completed`;
  const note = pending > 0
    ? `${pending} pending`
    : (missed > 0 ? `${missed} missed` : null);

  return { value: `${compliance_percent}%`, subtext, note, accent };
}

const RESPONSE_TOOLTIP = "Average office-hours-aware time between ticket creation and the technician's first response (system-generated activity is excluded). Tickets with no response yet are excluded.";
const RESOLUTION_FALLBACK_TOOLTIP = "No genuine technician response was recorded for this period, so this shows the average office-hours-aware time from ticket creation to completion (tasks.completed_at) instead, for the same technician ticket cohort.";

// Decides what the primary duration KPI card shows. The genuine
// technician-response metric always wins when it has data; the
// created->completed resolution-time fallback only substitutes in when NO
// genuine response was recorded for the period -- it is never blended into
// the response metric and never relabeled as a "response" (a ticket like
// #106, whose only technician-authored event was already excluded by the
// terminal-before-response fix, cannot resurface as a response through this
// fallback). "N/A" is shown only when neither metric has any eligible data.
function buildDurationCard(data, {
  respondedPhrase,
  resolvedPhrase = "",
  noResponseSubtext,
  responseTooltip = RESPONSE_TOOLTIP,
  resolutionTooltip = RESOLUTION_FALLBACK_TOOLTIP,
} = {}) {
  const response = data.average_response_time || {};
  const resolution = data.average_resolution_time || {};
  const responseCount = response.response_ticket_count ?? response.eligible_tickets ?? 0;
  const resolutionCount = resolution.resolution_ticket_count ?? resolution.eligible_tickets ?? 0;

  if (responseCount > 0) {
    return {
      title: "Average Response Time",
      value: response.formatted,
      subtext: `Based on ${responseCount} ticket(s) ${respondedPhrase}`,
      note: null,
      tooltip: responseTooltip,
    };
  }

  if (resolutionCount > 0) {
    return {
      title: "Average Resolution Time",
      value: resolution.formatted,
      subtext: `Based on ${resolutionCount} resolved ticket(s)${resolvedPhrase ? ` ${resolvedPhrase}` : ""}`,
      note: "First-response history unavailable",
      tooltip: resolutionTooltip,
    };
  }

  return {
    title: "Average Response Time",
    value: "N/A",
    subtext: noResponseSubtext || `Based on ${responseCount} ticket(s) ${respondedPhrase}`,
    note: null,
    tooltip: responseTooltip,
  };
}

const TechnicianPerformancePanel = () => {
  const { user } = useContext(AuthContext) || {};
  const { can } = usePermissions();
  const canViewAll = can("technician_performance", "view_all");
  const navigate = useNavigate();
  const goToPreventiveMaintenance = useCallback(() => navigate("/technician/preventive-maintenance"), [navigate]);

  const initial = currentMonthYear();
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  const [technicianId, setTechnicianId] = useState("self");
  const [technicianOptions, setTechnicianOptions] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Cumulative (multi-month) performance view ---
  const [viewMode, setViewMode] = useState("monthly"); // "monthly" | "cumulative"
  const defaultCumulativeFrom = shiftMonth(initial.month, initial.year, -2);
  const [fromMonth, setFromMonth] = useState(defaultCumulativeFrom.month);
  const [fromYear, setFromYear] = useState(defaultCumulativeFrom.year);
  const [toMonth, setToMonth] = useState(initial.month);
  const [toYear, setToYear] = useState(initial.year);
  const [rangeError, setRangeError] = useState(null);

  const [cumulativeData, setCumulativeData] = useState(null);
  const [cumulativeLoading, setCumulativeLoading] = useState(false);
  const [cumulativeError, setCumulativeError] = useState(null);

  useEffect(() => {
    if (!canViewAll) return;
    let cancelled = false;
    api
      .get("/technician-users")
      .then((res) => {
        if (!cancelled) setTechnicianOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setTechnicianOptions([]);
      });
    return () => { cancelled = true; };
  }, [canViewAll]);

  const fetchPerformance = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = { month, year };
      if (technicianId !== "self") params.technician_id = technicianId;
      const res = await api.get("/technician/dashboard/performance", { params });
      setData(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load performance metrics.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, month, year, technicianId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (viewMode === "monthly") fetchPerformance(); }, [viewMode, fetchPerformance]);

  const isRangeValid = monthKey(fromMonth, fromYear) <= monthKey(toMonth, toYear);

  const fetchCumulativePerformance = useCallback(async () => {
    if (!user?.id) return;
    if (!isRangeValid) {
      setRangeError("End month must be on or after start month.");
      return;
    }
    setRangeError(null);
    setCumulativeLoading(true);
    setCumulativeError(null);
    try {
      const params = { from_month: fromMonth, from_year: fromYear, to_month: toMonth, to_year: toYear };
      if (technicianId !== "self") params.technician_id = technicianId;
      const res = await api.get("/technician/dashboard/performance/cumulative", { params });
      setCumulativeData(res.data?.data || null);
    } catch (err) {
      setCumulativeError(err?.response?.data?.message || "Failed to load cumulative performance metrics.");
      setCumulativeData(null);
    } finally {
      setCumulativeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, fromMonth, fromYear, toMonth, toYear, technicianId, isRangeValid]);

  useEffect(() => {
    if (viewMode === "cumulative") fetchCumulativePerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, fetchCumulativePerformance]);

  const applyQuickRange = (kind) => {
    if (kind === "fy") {
      const fy = financialYearRange(initial.month, initial.year);
      setFromMonth(fy.from.month); setFromYear(fy.from.year);
      setToMonth(fy.to.month); setToYear(fy.to.year);
      return;
    }
    const monthsBack = { last3: 2, last4: 3, last6: 5, quarter: 2 }[kind] ?? 2;
    const from = shiftMonth(initial.month, initial.year, -monthsBack);
    setFromMonth(from.month); setFromYear(from.year);
    setToMonth(initial.month); setToYear(initial.year);
  };

  const trendChart = useMemo(() => {
    const trend = data?.monthly_trend || [];
    return {
      labels: trend.map((t) => t.month),
      datasets: [
        {
          label: "Avg Response (min)",
          data: trend.map((t) => t.average_response_minutes),
          borderColor: "#0ea5e9",
          backgroundColor: "#0ea5e9",
          yAxisID: "minutes",
          tension: 0.3,
        },
        {
          label: "Resolution Rate (%)",
          data: trend.map((t) => t.resolution_rate),
          borderColor: "#22c55e",
          backgroundColor: "#22c55e",
          yAxisID: "percent",
          tension: 0.3,
        },
        {
          label: "First Call Resolution (%)",
          data: trend.map((t) => t.first_call_resolution_rate),
          borderColor: "#8b5cf6",
          backgroundColor: "#8b5cf6",
          yAxisID: "percent",
          tension: 0.3,
        },
        {
          label: "Preventive Compliance (%)",
          // null points (preventive not available/configured for that
          // month) render as a genuine gap in the line, same convention as
          // the average-response-time series above -- never a fake 0.
          data: trend.map((t) => t.preventive_compliance_rate ?? null),
          borderColor: "#d97706",
          backgroundColor: "#d97706",
          yAxisID: "percent",
          tension: 0.3,
        },
      ],
    };
  }, [data]);

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // Extra top padding so the always-on point-value labels drawn by
    // pointValueLabelPlugin (above each point) never get clipped when a
    // line peaks near the top of the chart area.
    layout: { padding: { top: 18 } },
    plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 10 }, padding: 6 } } },
    scales: {
      minutes: { type: "linear", position: "left", beginAtZero: true, ticks: { font: { size: 10 } } },
      percent: { type: "linear", position: "right", beginAtZero: true, max: 100, grid: { drawOnChartArea: false }, ticks: { font: { size: 10 } } },
      x: { ticks: { font: { size: 10 } } },
    },
  };

  const responseCard = useMemo(() => (
    data ? buildDurationCard(data, { respondedPhrase: "with a technician response" }) : null
  ), [data]);

  const cumulativeResponseCard = useMemo(() => (
    cumulativeData
      ? buildDurationCard(cumulativeData, {
        respondedPhrase: "across the selected period",
        resolvedPhrase: "across the selected period",
        noResponseSubtext: "No responded tickets in this period",
        responseTooltip: "Total office-hours-aware response minutes across the selected period divided by the total number of responded tickets -- not an average of monthly averages.",
        resolutionTooltip: "No genuine technician response was recorded in this period, so this shows the total office-hours-aware creation-to-completion minutes across the period divided by the total number of resolved tickets instead -- not an average of monthly averages.",
      })
      : null
  ), [cumulativeData]);

  const preventiveCard = useMemo(() => buildPreventiveCard(data?.preventive_maintenance), [data]);
  const cumulativePreventiveCard = useMemo(() => buildPreventiveCard(cumulativeData?.preventive_maintenance), [cumulativeData]);

  const hasNoData = data
    && data.average_response_time.eligible_tickets === 0
    && data.resolution_rate.eligible_tickets === 0;

  const cumulativeHasNoData = cumulativeData
    && cumulativeData.average_response_time.eligible_tickets === 0
    && cumulativeData.resolution_rate.eligible_tickets === 0;

  // "Best"/"lowest" month per metric, using each metric's correct
  // direction: lower is better for response time, higher is better for
  // resolution/FCR rates. Only considers months that actually had eligible
  // tickets for that metric, so an empty month never wins by default.
  const monthlyComparison = useMemo(() => {
    const rows = cumulativeData?.monthly_breakdown || [];
    const pick = (key, eligibleKey, better) => {
      const candidates = rows.filter((r) => r[eligibleKey === "response" ? "average_response_time" : eligibleKey].eligible_tickets > 0);
      if (!candidates.length) return null;
      return candidates.reduce((best, r) => {
        const value = key === "response" ? r.average_response_time.minutes
          : key === "resolution" ? r.resolution_rate.percentage
          : r.first_call_resolution_rate.percentage;
        const bestValue = key === "response" ? best.average_response_time.minutes
          : key === "resolution" ? best.resolution_rate.percentage
          : best.first_call_resolution_rate.percentage;
        return better(value, bestValue) ? r : best;
      }, candidates[0]);
    };
    return {
      bestResponse: pick("response", "response", (v, b) => v < b),
      bestResolution: pick("resolution", "resolution_rate", (v, b) => v > b),
      lowestResolution: pick("resolution", "resolution_rate", (v, b) => v < b),
      bestFcr: pick("fcr", "first_call_resolution_rate", (v, b) => v > b),
    };
  }, [cumulativeData]);

  return (
    <div className="td-card tpp-panel">
      <div className="td-ch">
        <strong>Performance</strong>
        <div className="tpp-view-switch" role="group" aria-label="Performance View">
          <button
            type="button"
            className={`tpp-view-btn${viewMode === "monthly" ? " tpp-view-btn--active" : ""}`}
            onClick={() => setViewMode("monthly")}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`tpp-view-btn${viewMode === "cumulative" ? " tpp-view-btn--active" : ""}`}
            onClick={() => setViewMode("cumulative")}
          >
            Cumulative
          </button>
        </div>
      </div>

      <div className="tpp-toolbar">
        {viewMode === "monthly" && (
          <div className="tpp-filters">
            <Form.Select className="td-sel" aria-label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
            </Form.Select>
            <Form.Select className="td-sel" aria-label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions(initial.year).map((y) => <option key={y} value={y}>{y}</option>)}
            </Form.Select>
            {canViewAll && (
              <Form.Select className="td-sel" aria-label="Technician" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
                <option value="self">My Performance</option>
                <option value="all">All Technicians</option>
                {technicianOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name || `Technician #${t.id}`}</option>
                ))}
              </Form.Select>
            )}
            <Button variant={null} className="td-btn td-btn-ghost" onClick={fetchPerformance} disabled={loading}>
              <i className="fa-solid fa-rotate" aria-hidden="true"></i>{loading ? "..." : "Refresh"}
            </Button>
          </div>
        )}

        {viewMode === "cumulative" && (
          <>
            {canViewAll && (
              <div className="tpp-filters">
                <Form.Select className="td-sel" aria-label="Technician" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
                  <option value="self">My Performance</option>
                  <option value="all">All Technicians</option>
                  {technicianOptions.map((t) => (
                    <option key={t.id} value={t.id}>{t.name || `Technician #${t.id}`}</option>
                  ))}
                </Form.Select>
              </div>
            )}

            <div className="tpp-period-row">
              <span className="tpp-period-label">Period</span>
              <Form.Select className="td-sel td-sel-sm" value={monthKey(fromMonth, fromYear)} onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                setFromMonth(m); setFromYear(y);
              }}>
                {yearOptions(initial.year).flatMap((y) => MONTH_NAMES.map((name, i) => (
                  <option key={`${y}-${i + 1}`} value={monthKey(i + 1, y)}>{name.slice(0, 3)} {y}</option>
                )))}
              </Form.Select>
              <span className="tpp-period-arrow">→</span>
              <Form.Select className="td-sel td-sel-sm" value={monthKey(toMonth, toYear)} onChange={(e) => {
                const [y, m] = e.target.value.split("-").map(Number);
                setToMonth(m); setToYear(y);
              }}>
                {yearOptions(initial.year).flatMap((y) => MONTH_NAMES.map((name, i) => (
                  <option key={`${y}-${i + 1}`} value={monthKey(i + 1, y)}>{name.slice(0, 3)} {y}</option>
                )))}
              </Form.Select>
              <Button variant={null} className="td-btn td-btn-primary" onClick={fetchCumulativePerformance} disabled={cumulativeLoading}>
                <i className="fa-solid fa-check" aria-hidden="true"></i>{cumulativeLoading ? "..." : "Apply"}
              </Button>
            </div>

            <div className="tpp-quick-options">
              <button type="button" className="tpp-quick-btn" onClick={() => applyQuickRange("last3")}>Last 3 Months</button>
              <button type="button" className="tpp-quick-btn" onClick={() => applyQuickRange("last4")}>Last 4 Months</button>
              <button type="button" className="tpp-quick-btn" onClick={() => applyQuickRange("last6")}>Last 6 Months</button>
              <button type="button" className="tpp-quick-btn" onClick={() => applyQuickRange("quarter")}>Quarter</button>
              <button type="button" className="tpp-quick-btn" onClick={() => applyQuickRange("fy")}>Financial Year</button>
            </div>

            {rangeError && <div className="tpp-range-error">{rangeError}</div>}
          </>
        )}
      </div>

      {viewMode === "monthly" && loading && !data && (
        <div className="td-loader"><Spinner animation="border" size="sm" /><p>Loading performance metrics…</p></div>
      )}

      {viewMode === "monthly" && !loading && error && (
        <div className="td-empty">
          <div className="td-ei"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i></div>
          <p>{error}</p>
          <Button variant={null} className="td-btn td-btn-primary td-btn-xs" style={{ marginTop: 6 }} onClick={fetchPerformance}>
            <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>Try Again
          </Button>
        </div>
      )}

      {viewMode === "monthly" && !error && data && hasNoData && (
        <div className="td-empty">
          <div className="td-ei"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div>
          <p>No tickets found for {MONTH_NAMES[month - 1]} {year}.</p>
        </div>
      )}

      {viewMode === "monthly" && !error && data && !hasNoData && (
        <>
          <div className="tpp-kpi-grid">
            <KpiCard
              title={responseCard.title}
              value={responseCard.value}
              subtext={responseCard.subtext}
              note={responseCard.note}
              tooltip={responseCard.tooltip}
              accent="blue"
            />
            <KpiCard
              title="Ticket Resolution Rate"
              value={`${data.resolution_rate.percentage}%`}
              subtext={`${data.resolution_rate.resolved_tickets} resolved out of ${data.resolution_rate.eligible_tickets} tickets`}
              tooltip="Percentage of tickets created this month that are currently in a resolved/closed status. Cancelled tickets are excluded."
              accent="green"
            />
            <KpiCard
              title="First Call Resolution Rate"
              value={`${data.first_call_resolution_rate.percentage}%`}
              subtext={`${data.first_call_resolution_rate.first_call_resolved_tickets} of ${data.first_call_resolution_rate.resolved_tickets} resolved tickets`}
              tooltip="Percentage of this month's resolved tickets that were resolved during a single handling cycle -- no reassignment, delegation, escalation, reopening, or multiple technicians involved."
              accent="purple"
            />
            <KpiCard
              title="Preventive Compliance"
              value={preventiveCard.value}
              subtext={preventiveCard.subtext}
              note={preventiveCard.note}
              tooltip="Share of this month's required monthly preventive checks (Antivirus Update, Patch Update, etc.) confirmed completed. A partially-completed current month is Pending, never Missed -- that label only applies once the month has closed."
              accent={preventiveCard.accent}
              onClick={goToPreventiveMaintenance}
            />
          </div>

          <div className="tpp-trend">
            <div className="td-ch"><strong>6-Month Trend</strong></div>
            <div className="td-chartbox td-chartbox--lg">
              {data.monthly_trend?.length ? (
                <Line data={trendChart} options={trendOptions} plugins={[pointValueLabelPlugin]} />
              ) : (
                <div className="td-empty"><p>No trend data available.</p></div>
              )}
            </div>
          </div>
        </>
      )}

      {viewMode === "cumulative" && cumulativeLoading && !cumulativeData && (
        <div className="td-loader"><Spinner animation="border" size="sm" /><p>Loading cumulative performance metrics…</p></div>
      )}

      {viewMode === "cumulative" && !cumulativeLoading && cumulativeError && (
        <div className="td-empty">
          <div className="td-ei"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i></div>
          <p>{cumulativeError}</p>
          <Button variant={null} className="td-btn td-btn-primary td-btn-xs" style={{ marginTop: 6 }} onClick={fetchCumulativePerformance}>
            <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>Try Again
          </Button>
        </div>
      )}

      {viewMode === "cumulative" && !cumulativeError && cumulativeData && (
        <>
          <div className="tpp-period-caption">
            Cumulative Performance<br />
            <strong>
              {formatMonthLabel(cumulativeData.period.from_month, cumulativeData.period.from_year)}
              {" – "}
              {formatMonthLabel(cumulativeData.period.to_month, cumulativeData.period.to_year)}
            </strong>
            {" • "}{cumulativeData.period.month_count} Month{cumulativeData.period.month_count === 1 ? "" : "s"}
          </div>

          {cumulativeHasNoData ? (
            <div className="td-empty">
              <div className="td-ei"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div>
              <p>No eligible records for the selected period.</p>
            </div>
          ) : (
            <>
              <div className="tpp-kpi-grid">
                <KpiCard
                  title={cumulativeResponseCard.title}
                  value={cumulativeResponseCard.value}
                  subtext={cumulativeResponseCard.subtext}
                  note={cumulativeResponseCard.note}
                  tooltip={cumulativeResponseCard.tooltip}
                  accent="blue"
                />
                <KpiCard
                  title="Ticket Resolution Rate"
                  value={cumulativeData.resolution_rate.eligible_tickets === 0 ? "—" : `${cumulativeData.resolution_rate.percentage}%`}
                  subtext={`${cumulativeData.resolution_rate.resolved_tickets} resolved out of ${cumulativeData.resolution_rate.eligible_tickets} tickets`}
                  tooltip="Combined resolved / combined eligible tickets across the selected period -- not an average of monthly percentages."
                  accent="green"
                />
                <KpiCard
                  title="First Call Resolution Rate"
                  value={cumulativeData.first_call_resolution_rate.resolved_tickets === 0 ? "—" : `${cumulativeData.first_call_resolution_rate.percentage}%`}
                  subtext={`${cumulativeData.first_call_resolution_rate.first_call_resolved_tickets} of ${cumulativeData.first_call_resolution_rate.resolved_tickets} resolved tickets`}
                  tooltip="Combined first-call-resolved / combined resolved tickets across the selected period -- not an average of monthly percentages."
                  accent="purple"
                />
                <KpiCard
                  title="Preventive Compliance"
                  value={cumulativePreventiveCard.value}
                  subtext={cumulativePreventiveCard.subtext}
                  note={cumulativePreventiveCard.note}
                  tooltip="Combined completed / combined required preventive checks across the selected period -- not an average of monthly percentages."
                  accent={cumulativePreventiveCard.accent}
                  onClick={goToPreventiveMaintenance}
                />
              </div>

              {cumulativeData.monthly_breakdown?.length > 1 && (
                <div className="tpp-comparison">
                  {monthlyComparison.bestResponse && (
                    <span>Best Response: <strong>{monthlyComparison.bestResponse.month}</strong> — {monthlyComparison.bestResponse.average_response_time.formatted}</span>
                  )}
                  {monthlyComparison.bestResolution && (
                    <span>Best Resolution: <strong>{monthlyComparison.bestResolution.month}</strong> — {monthlyComparison.bestResolution.resolution_rate.percentage}%</span>
                  )}
                  {monthlyComparison.lowestResolution && monthlyComparison.lowestResolution.month !== monthlyComparison.bestResolution?.month && (
                    <span>Lowest Resolution: <strong>{monthlyComparison.lowestResolution.month}</strong> — {monthlyComparison.lowestResolution.resolution_rate.percentage}%</span>
                  )}
                  {monthlyComparison.bestFcr && (
                    <span>Best FCR: <strong>{monthlyComparison.bestFcr.month}</strong> — {monthlyComparison.bestFcr.first_call_resolution_rate.percentage}%</span>
                  )}
                </div>
              )}

              <div className="tpp-breakdown">
                <div className="td-ch"><strong>Monthly Breakdown</strong></div>
                <div className="tpp-breakdown-table-wrap">
                  <table className="tpp-breakdown-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Avg Response</th>
                        <th>Resolution</th>
                        <th>FCR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cumulativeData.monthly_breakdown.map((row) => (
                        <tr key={row.month}>
                          <td>{row.month}</td>
                          <td>{row.average_response_time.eligible_tickets === 0 ? "N/A" : row.average_response_time.formatted}</td>
                          <td>{row.resolution_rate.eligible_tickets === 0 ? "—" : `${row.resolution_rate.percentage}%`}</td>
                          <td>{row.first_call_resolution_rate.resolved_tickets === 0 ? "—" : `${row.first_call_resolution_rate.percentage}%`}</td>
                        </tr>
                      ))}
                      <tr className="tpp-breakdown-total">
                        <td>Cumulative</td>
                        <td>{cumulativeData.average_response_time.eligible_tickets === 0 ? "N/A" : cumulativeData.average_response_time.formatted}</td>
                        <td>{cumulativeData.resolution_rate.eligible_tickets === 0 ? "—" : `${cumulativeData.resolution_rate.percentage}%`}</td>
                        <td>{cumulativeData.first_call_resolution_rate.resolved_tickets === 0 ? "—" : `${cumulativeData.first_call_resolution_rate.percentage}%`}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Phase 52: New Joiner Device Deployment -- own-technician data,
          always shown regardless of the Monthly/Cumulative toggle above
          (it has its own independent joining-date SLA period, not a
          month/range the existing toolbar controls). Deliberately its own
          section, never merged into tpp-kpi-grid, so this joining-date SLA
          is never confused with the ticket-response TAT cards above it. */}
      <NewJoinerDeviceDeploymentCard />

      <style>{`
        .tpp-panel{margin-top:0;margin-bottom:16px;}
        .tpp-filters{display:flex;gap:8px;flex-wrap:wrap;align-items:center;}
        /* Form.Select (Bootstrap .form-select) defaults to width:100%, which --
           with no override -- forces Month/Year/Technician to each claim a
           full row instead of sitting together, since flex-wrap only wraps
           once an item's own basis exceeds the remaining row space. Bounding
           width here keeps them side by side on desktop; the existing
           640px media query below still switches them back to flex:1 1 auto
           (full-width, stacked) on mobile. */
        .tpp-filters .td-sel{width:auto;flex:0 0 auto;min-width:130px;}
        .tpp-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;padding:16px;}
        .tpp-kpi-card{padding:16px;box-shadow:none;border-radius:14px;min-height:120px;}
        .tpp-kpi-card--blue{border-color:#bfdbfe;background:#eff6ff;}
        .tpp-kpi-card--green{border-color:#bbf7d0;background:#ecfdf5;}
        .tpp-kpi-card--purple{border-color:#e9d5ff;background:#f5f3ff;}
        .tpp-kpi-card--amber{border-color:#fde68a;background:#fffbeb;}
        /* KpiCard renders a real <button> (not a styled div) when it's
           given an onClick -- reset the native button chrome so it looks
           identical to the other, non-clickable KPI cards, plus a subtle
           hover/focus affordance so it still reads as interactive. */
        button.tpp-kpi-card{width:100%;text-align:left;font:inherit;cursor:pointer;}
        .tpp-kpi-card--clickable:hover{box-shadow:0 0 0 2px rgba(37,99,235,.15);}
        .tpp-kpi-card--clickable:focus-visible{outline:2px solid #2563eb;outline-offset:2px;}
        .tpp-kpi-head{display:flex;align-items:center;justify-content:space-between;gap:6px;}
        .tpp-kpi-title{font-size:12px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.05em;}
        .tpp-info{color:#94a3b8;cursor:help;display:inline-flex;flex-shrink:0;}
        .tpp-kpi-value{font-size:28px;font-weight:700;color:#14151f;margin-top:6px;line-height:1.15;}
        .tpp-kpi-card--blue .tpp-kpi-value{color:#2563eb;}
        .tpp-kpi-card--green .tpp-kpi-value{color:#16a34a;}
        .tpp-kpi-card--purple .tpp-kpi-value{color:#7c3aed;}
        .tpp-kpi-card--amber .tpp-kpi-value{color:#b45309;}
        .tpp-kpi-sub{font-size:11.5px;color:#64748b;font-weight:600;margin-top:4px;}
        .tpp-kpi-note{font-size:10.5px;color:#94a3b8;font-weight:600;font-style:italic;margin-top:2px;}
        .tpp-trend{border-top:1px solid #f1f5f9;}
        .tpp-view-switch{display:inline-flex;flex-shrink:0;border:1px solid #e3c7d0;border-radius:8px;overflow:hidden;height:40px;}
        .tpp-view-btn{border:none;background:#fff;color:#7a1236;font-size:11px;font-weight:700;padding:0 14px;cursor:pointer;height:100%;}
        .tpp-view-btn--active{background:#7a1236;color:#fff;}
        .tpp-view-btn:focus-visible{outline:2px solid #7a1236;outline-offset:-2px;}
        /* Dedicated toolbar row below the title/toggle header -- keeps mode
           switching (always in the header) structurally separate from the
           mode-specific filters here, so neither crowds the other and the
           filters never need to wrap inside the header row itself. */
        .tpp-toolbar{padding:10px 16px 14px;border-bottom:1px solid #f1f5f9;}
        .tpp-period-row{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
        .tpp-period-row + .tpp-quick-options{margin-top:8px;}
        .tpp-period-label{font-size:11px;font-weight:800;color:#64748b;}
        .tpp-period-arrow{color:#94a3b8;font-weight:700;}
        .td-sel-sm{max-width:130px;}
        .tpp-quick-options{display:flex;gap:6px;flex-wrap:wrap;}
        .tpp-quick-btn{border:1px solid #e2e8f0;background:#f8fafc;color:#334155;font-size:10px;font-weight:700;padding:3px 8px;border-radius:12px;cursor:pointer;}
        .tpp-quick-btn:hover{background:#eef2f7;}
        .tpp-range-error{color:#dc2626;font-size:11px;font-weight:700;margin-top:6px;}
        .tpp-period-caption{padding:8px 10px 0;font-size:11px;color:#64748b;font-weight:600;}
        .tpp-period-caption strong{font-size:13px;color:#14151f;}
        .tpp-comparison{display:flex;gap:14px;flex-wrap:wrap;padding:0 10px 6px;font-size:11px;color:#475569;font-weight:600;}
        .tpp-comparison strong{color:#14151f;}
        .tpp-breakdown{border-top:1px solid #f1f5f9;min-width:0;}
        .tpp-breakdown-table-wrap{width:100%;max-width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;padding:0 10px 10px;box-sizing:border-box;}
        .tpp-breakdown-table{width:100%;min-width:600px;table-layout:fixed;border-collapse:collapse;font-size:12px;}
        .tpp-breakdown-table th{text-align:left;color:#fff;font-weight:700;text-transform:uppercase;font-size:12px;letter-spacing:.04em;height:44px;padding:0 16px;background:#7a1236;border-bottom:none;vertical-align:middle;}
        .tpp-breakdown-table td{padding:12px 16px;border-bottom:1px solid #f1f5f9;color:#14151f;font-weight:600;vertical-align:middle;min-height:56px;}
        .tpp-breakdown-table tbody tr{min-height:56px;}
        .tpp-breakdown-table tbody tr:hover{background:#f8fafc;}
        .tpp-breakdown-total td{font-weight:900;border-top:2px solid #e2e8f0;background:#f8fafc;}
        .tpp-breakdown-table th:nth-child(1),.tpp-breakdown-table td:nth-child(1){width:25%;white-space:nowrap;}
        .tpp-breakdown-table th:nth-child(2),.tpp-breakdown-table td:nth-child(2){width:30%;}
        .tpp-breakdown-table th:nth-child(3),.tpp-breakdown-table td:nth-child(3){width:22.5%;}
        .tpp-breakdown-table th:nth-child(4),.tpp-breakdown-table td:nth-child(4){width:22.5%;}
        .tpp-breakdown-table th:nth-child(n+2),.tpp-breakdown-table td:nth-child(n+2){text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;}
        @media(max-width:900px){.tpp-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
        @media(max-width:640px){
          .tpp-filters{width:100%;}
          .tpp-filters .td-sel{flex:1 1 auto;}
          .tpp-kpi-grid{grid-template-columns:1fr;}
        }
      `}</style>
    </div>
  );
};

export default TechnicianPerformancePanel;
